/* eslint-disable max-lines */
import { EventBus, Events } from "jiffy-store";
import { getWindowWidth } from "common/utils/browser";
import { trackEvent } from "../made-category-print-analytics";
import initSpeedBenefitsMap, { refreshSpeedBenefitsMapFromZipChange } from "./speed-map";
import initBenefitsSwitcherWheel from "./benefits-switcher-wheel";

const SWITCHER_SELECTOR = "[data-benefits-switcher]";
const TAB_SELECTOR = "[data-benefits-switcher-tab]";
const PANEL_SELECTOR = "[data-benefits-switcher-panel]";
const MEDIA_SELECTOR = "[data-benefits-switcher-media]";
const SPEED_MAP_SELECTOR = "[data-benefits-switcher-speed-map]";
const VIDEO_SKELETON_SELECTOR = "[data-benefits-switcher-video-skeleton]";

const SPEED_MAP_REFRESH_MAX_WIDTH = 1024;

const ACTIVE_PANEL_CLASSES = ["opacity-100", "translate-y-0", "pointer-events-auto"];
const INACTIVE_PANEL_CLASSES = ["opacity-0", "translate-y-3", "pointer-events-none"];

const ACTIVE_MEDIA_CLASSES = ["opacity-100", "translate-x-0", "pointer-events-auto", "touch-pan-y"];
const INACTIVE_MEDIA_CLASSES = ["opacity-0", "translate-x-4", "pointer-events-none", "touch-none"];

let listenersAttached = false;
let cleanupListenersAttached = false;
const wheelInitialized = new WeakSet();
const wheelApis = new WeakMap();
const videoSkeletonBound = new WeakSet();
const pendingPlayHandlers = new WeakMap();

const $switchers = () => Array.from(document.querySelectorAll(SWITCHER_SELECTOR));
const $closestSwitcher = $el => $el?.closest(SWITCHER_SELECTOR);
const $closestTab = $el => $el?.closest?.(TAB_SELECTOR);
const $tabs = $switcher => Array.from($switcher.querySelectorAll(TAB_SELECTOR));
const $panels = $switcher => Array.from($switcher.querySelectorAll(PANEL_SELECTOR));
const $mediaItems = $switcher => Array.from($switcher.querySelectorAll(MEDIA_SELECTOR));
const $speedMap = $switcher => $switcher.querySelector(SPEED_MAP_SELECTOR);
const $videoSkeleton = $mediaItem => $mediaItem.querySelector(VIDEO_SKELETON_SELECTOR);
const $video = $mediaItem => $mediaItem.querySelector(".js-benefits-switcher-video");

function hideVideoSkeleton($mediaItem) {
  const $skeleton = $videoSkeleton($mediaItem);
  if (!$skeleton) return;

  $skeleton.classList.remove("animate-pulse");
  // Force a reflow so the browser registers the removal before adding the transition
  // eslint-disable-next-line no-unused-expressions
  $skeleton.offsetHeight;
  $skeleton.classList.add("opacity-0", "transition-opacity", "duration-300");
  $skeleton.addEventListener("transitionend", () => $skeleton.remove(), { once: true });
}

function bindVideoSkeletonListeners($mediaItem) {
  const $vid = $video($mediaItem);
  if (!$vid || videoSkeletonBound.has($vid)) return;
  videoSkeletonBound.add($vid);

  if ($vid.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA && !$vid.paused) {
    hideVideoSkeleton($mediaItem);
    return;
  }

  $vid.addEventListener("playing", () => hideVideoSkeleton($mediaItem), { once: true });
}

function cancelPlayWhenReady($vid) {
  const handler = pendingPlayHandlers.get($vid);
  if (handler) {
    $vid.removeEventListener("canplay", handler);
    pendingPlayHandlers.delete($vid);
  }
}

function playWhenReady($vid) {
  cancelPlayWhenReady($vid);
  $vid.play().catch(() => {
    if ($vid.readyState < HTMLMediaElement.HAVE_FUTURE_DATA) {
      const handler = () => {
        pendingPlayHandlers.delete($vid);
        $vid.play().catch(() => {});
      };
      pendingPlayHandlers.set($vid, handler);
      $vid.addEventListener("canplay", handler, { once: true });
    }
  });
}

function setActiveEl(el, isActive, classMap) {
  if (!el) return;
  const $el = el;
  $el.hidden = false;
  if ($el.hasAttribute("tabindex")) {
    $el.tabIndex = isActive ? 0 : -1;
  }

  $el.setAttribute("aria-hidden", String(!isActive));
  if ("inert" in $el) $el.inert = !isActive;

  $el.classList.remove(...classMap.active, ...classMap.inactive);
  $el.classList.add(...(isActive ? classMap.active : classMap.inactive));
}

function activate($switcher, name) {
  for (const $tab of $tabs($switcher)) {
    const isActive = $tab.dataset.benefitsSwitcherTab === name;
    $tab.setAttribute("aria-selected", String(isActive));
    $tab.tabIndex = isActive ? 0 : -1;
  }

  for (const $panel of $panels($switcher)) {
    const isActive = $panel.dataset.benefitsSwitcherPanel === name;
    setActiveEl($panel, isActive, {
      active: ACTIVE_PANEL_CLASSES,
      inactive: INACTIVE_PANEL_CLASSES
    });
  }

  for (const $item of $mediaItems($switcher)) {
    const isActive = $item.dataset.benefitsSwitcherMedia === name;
    setActiveEl($item, isActive, {
      active: ACTIVE_MEDIA_CLASSES,
      inactive: INACTIVE_MEDIA_CLASSES
    });

    const $vid = $video($item);
    if ($vid) {
      if (isActive) {
        playWhenReady($vid);
      } else {
        cancelPlayWhenReady($vid);
        $vid.pause();
      }
    }
  }

  if (name === "speed") {
    const $mapEl = $speedMap($switcher);
    if ($mapEl) initSpeedBenefitsMap($mapEl);
  }
}

function getDefaultTabName($switcher) {
  const $allTabs = $tabs($switcher);
  if ($allTabs.length === 0) return null;
  const $selected = $allTabs.find(t => t.getAttribute("aria-selected") === "true") || $allTabs[0];
  return $selected?.dataset?.benefitsSwitcherTab;
}

function getActiveTabIndex($allTabs) {
  const selectedIndex = $allTabs.findIndex(t => t.getAttribute("aria-selected") === "true");
  return selectedIndex === -1 ? 0 : selectedIndex;
}

function activateFromTab($tab) {
  const name = $tab?.dataset?.benefitsSwitcherTab;
  if (!name) return;
  const $sw = $closestSwitcher($tab);
  if (!$sw) return;
  activate($sw, name);
  wheelApis.get($sw)?.scrollToTab(name);
}

function onClick(event) {
  const $tab = $closestTab(event.target);
  if (!$tab) return;
  const tabName = $tab.dataset.benefitsSwitcherTab;
  activateFromTab($tab);
  if (!tabName) return;
  trackEvent("clp_benefits_switcher_tab_clicked", {
    section_identifier: "benefits_switcher",
    tab: tabName,
    eventLabel: tabName
  });
}

function onKeydown(event) {
  const $tab = $closestTab(event.target);
  if (!$tab) return;

  const isPrev = event.key === "ArrowUp" || event.key === "ArrowLeft";
  const isNext = event.key === "ArrowDown" || event.key === "ArrowRight";
  const isHome = event.key === "Home";
  const isEnd = event.key === "End";
  if (!isPrev && !isNext && !isHome && !isEnd) return;

  const $sw = $closestSwitcher($tab);
  if (!$sw) return;

  const $allTabs = $tabs($sw);
  const currentIndex = $allTabs.indexOf($tab);
  if (currentIndex === -1 || $allTabs.length === 0) return;

  event.preventDefault();

  let $nextTab = null;
  if (isHome) [$nextTab] = $allTabs;
  else if (isEnd) $nextTab = $allTabs[$allTabs.length - 1];
  else {
    const direction = isNext ? 1 : -1;
    $nextTab = $allTabs[(currentIndex + direction + $allTabs.length) % $allTabs.length];
  }

  $nextTab?.focus();
  if ($nextTab) activateFromTab($nextTab);
}

function getActiveTabName($switcher) {
  const $selected = $tabs($switcher).find($t => $t.getAttribute("aria-selected") === "true");
  return $selected?.dataset?.benefitsSwitcherTab;
}

function onZipcodeUpdated() {
  if (getWindowWidth() > SPEED_MAP_REFRESH_MAX_WIDTH) return;
  for (const $switcher of $switchers()) {
    if (getActiveTabName($switcher) === "speed") {
      const $mapEl = $speedMap($switcher);
      if ($mapEl) refreshSpeedBenefitsMapFromZipChange($mapEl).catch(() => {});
    }
  }
}

function ensureListeners() {
  if (listenersAttached) return;
  listenersAttached = true;
  document.addEventListener("click", onClick);
  document.addEventListener("keydown", onKeydown);
  EventBus.on(Events.ZIPCODE_UPDATED, onZipcodeUpdated);
}

function destroyWheelForSwitcher($switcher) {
  const wheelApi = wheelApis.get($switcher);
  wheelApi?.destroy?.();
  wheelApis.delete($switcher);
  wheelInitialized.delete($switcher);
}

function destroyWheelsBeforeCache() {
  for (const $switcher of $switchers()) {
    destroyWheelForSwitcher($switcher);
  }
}

function ensureCleanupListeners() {
  if (cleanupListenersAttached) return;
  cleanupListenersAttached = true;
  document.addEventListener("turbolinks:before-cache", destroyWheelsBeforeCache);
  document.addEventListener("turbo:before-cache", destroyWheelsBeforeCache);
}

function getActiveNameIndex($switcher, tabNames) {
  const $allTabs = $tabs($switcher);
  const activeIdx = getActiveTabIndex($allTabs);
  const activeName = $allTabs[activeIdx]?.dataset?.benefitsSwitcherTab;
  const nameIdx = tabNames.indexOf(activeName);
  return nameIdx >= 0 ? nameIdx : 0;
}

function initWheelForSwitcher($switcher) {
  if (wheelInitialized.has($switcher)) return;

  const $allTabs = $tabs($switcher);
  const tabNames = $allTabs.map(t => t.dataset.benefitsSwitcherTab).filter(Boolean);
  if (tabNames.length === 0) return;

  const api = initBenefitsSwitcherWheel({
    sectionEl: $switcher,
    tabNames,
    getActiveIndex: () => getActiveNameIndex($switcher, tabNames),
    onSlideChange: name => activate($switcher, name)
  });

  if (api) wheelApis.set($switcher, api);
  wheelInitialized.add($switcher);
}

export default function initBenefitsSwitcher() {
  ensureListeners();
  ensureCleanupListeners();

  for (const $switcher of $switchers()) {
    const defaultName = getDefaultTabName($switcher);
    if (defaultName) activate($switcher, defaultName);

    for (const $item of $mediaItems($switcher)) {
      bindVideoSkeletonListeners($item);
    }

    initWheelForSwitcher($switcher);
  }
}
